/**
 * Text inside shapes (Publisher: Edit Shape ▸ Edit Text / right-click ▸ Edit Text / double-click).
 * A shape keeps its text and text formatting in its own `properties` (same keys as a text box); the renderer
 * lays the text out in an inset "text rectangle" on top of the shape geometry.
 */

/** Text-related property keys copied from a shape into the text overlay (everything else is shape formatting). */
export const SHAPE_TEXT_KEYS = [
  'text', 'html', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration', 'color', 'textAlign', 'verticalAlign',
  'lineHeight', 'letterSpacing', 'tracking', 'textScale', 'kerning', 'kernAbove', 'textTransform', 'fontVariant', 'fontVariantPosition',
  'textFill', 'textOutline', 'textEffects', 'listStyle', 'indentLeft', 'indentRight', 'firstLineIndent', 'spaceBefore', 'spaceAfter',
  'hyphenation', 'columns', 'columnGap', 'columnRule', 'columnRuleColor', 'autofit', 'tabStops', 'defaultTabStop', 'alignToBaselines',
  'ligatures', 'stylisticSet', 'swash', 'stylisticAlternates', 'numberStyle', 'textDirection', 'hyperlink',
];

export const SHAPE_TEXT_DEFAULTS = {
  fontFamily: 'Inter', fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none', color: '#111827',
  textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.2, letterSpacing: 0,
};

/** Open paths (lines, connectors, curves, arcs) cannot hold text; every closed shape can. */
export const canHoldText = (o) => !!o && o.type === 'shape' && !/^(line|line-arrow|line-double-arrow|elbow-connector|elbow-arrow|curved-connector|curved-arrow-line|curve|arc)$/.test(o.shapeType || '') && !(o.shapeType === 'freeform' && o.properties?.freeform?.noFill);

/** True when the shape carries visible text. */
export const hasShapeText = (o) => {
  if (!o || o.type !== 'shape') return false;
  const p = o.properties || {};
  if (p.html) return !!p.html.replace(/<[^>]+>|&nbsp;|\s/g, '');
  return !!(p.text && String(p.text).trim());
};

/**
 * Inset of the text rectangle as fractions of the shape frame { l, t, r, b }, roughly following Office's per-shape text
 * rectangles: rectangles use the whole frame, ovals the inscribed rectangle, pointed shapes leave their tips free.
 * Shape ids are those of `lib/shapes.js` (SHAPE_CATEGORIES).
 */
export const shapeTextInset = (shapeType = '') => {
  const t = shapeType;
  if (/^(rectangle|rounded-rect|frame|plaque|bevel|folded-corner|fc-process|fc-alternate-process|fc-predefined-process|fc-internal-storage|fc-card|fc-punched-tape|fc-display|textbox|freeform)$/.test(t)) return { l: 0.04, t: 0.04, r: 0.04, b: 0.04 };
  if (/^(circle|donut|no-symbol|smiley|sun|moon|heart|tear|pie|chord|block-arc|fc-connector|fc-or|fc-summing-junction|fc-terminator|fc-delay|fc-magnetic-disk|fc-direct-access-storage|fc-stored-data|fc-sequential-storage)$/.test(t)) return { l: 0.15, t: 0.15, r: 0.15, b: 0.15 };
  if (/^(diamond|fc-decision|cross|lightning|pentagon|hexagon|heptagon|octagon|decagon|dodecagon|star-\d+|explosion-\d+|quad-arrow|left-right-up-arrow|circular-arrow|u-turn-arrow)$/.test(t)) return { l: 0.22, t: 0.22, r: 0.22, b: 0.22 };
  if (/^(triangle|fc-extract|fc-merge)$/.test(t)) return { l: 0.25, t: 0.45, r: 0.25, b: 0.06 };
  if (/^right-triangle$/.test(t)) return { l: 0.08, t: 0.45, r: 0.3, b: 0.06 };
  if (/^callout-/.test(t)) return { l: 0.1, t: 0.1, r: 0.1, b: 0.3 };
  if (/arrow|chevron|parallelogram|trapezoid|fc-data|fc-manual-input|fc-manual-operation|fc-preparation|fc-off-page-connector|ribbon|scroll|wave|can|cube/.test(t)) return { l: 0.18, t: 0.18, r: 0.18, b: 0.18 };
  if (/^fc-/.test(t)) return { l: 0.1, t: 0.1, r: 0.1, b: 0.1 };
  return { l: 0.12, t: 0.12, r: 0.12, b: 0.12 };
};

/** A text-box-like object describing the shape's text overlay (used by the renderer, formatting commands and layout). */
export const shapeTextObject = (o) => {
  const p = o.properties || {};
  const props = { ...SHAPE_TEXT_DEFAULTS };
  for (const k of SHAPE_TEXT_KEYS) if (p[k] !== undefined) props[k] = p[k];
  const ins = shapeTextInset(o.shapeType);
  props.paddingLeft = Math.round(ins.l * o.width);
  props.paddingRight = Math.round(ins.r * o.width);
  props.paddingTop = Math.round(ins.t * o.height);
  props.paddingBottom = Math.round(ins.b * o.height);
  if (props.text === undefined) props.text = '';
  return { ...o, type: 'textbox', properties: props };
};
